<template>
  <footer class="message-composer">
    <div class="message-composer__tools">
      <z-popover
        placement="top-start"
        trigger="click"
        :width="280"
        :show-arrow="false"
      >
        <template #reference>
          <z-button link aria-label="表情">😊</z-button>
        </template>
        <div class="message-composer__emoji">
          <button
            v-for="item in EMOJIS"
            :key="item"
            type="button"
            class="message-composer__emoji-item"
            @click="insertEmoji(item)"
          >
            {{ item }}
          </button>
        </div>
      </z-popover>
      <z-button :icon="Picture" link aria-label="图片" @click="pickImage" />
      <z-button :icon="Paperclip" link aria-label="文件" @click="pickFile" />
      <input
        ref="imageInputRef"
        type="file"
        accept="image/*"
        hidden
        @change="onImageChange"
      />
      <input
        ref="fileInputRef"
        type="file"
        hidden
        @change="onFileChange"
      />
    </div>
    <div class="message-composer__input">
      <z-input
        ref="inputRef"
        v-model="draft"
        type="textarea"
        resize="none"
        :autosize="{ minRows: 8, maxRows: 8 }"
        placeholder="输入消息，Enter 发送"
        @keydown="onKeydown"
      />
      <z-button type="primary" :disabled="!canSend" @click="submitText">
        发送
      </z-button>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { Paperclip, Picture } from '@zoryn/icons'
import ZButton from '@zoryn/components/button'
import ZInput from '@zoryn/components/input'
import type { InputInstance } from '@zoryn/components/input'
import ZPopover from '@zoryn/components/popover'
import { appendMemberMention } from '../utils/conversation-utils'
import '@zoryn/components/button/style'
import '@zoryn/components/input/style'
import '@zoryn/components/popover/style'
import '@zoryn/components/tooltip/style'

const EMOJIS = [
  '😀',
  '😁',
  '😂',
  '😊',
  '😍',
  '👌',
  '👍',
  '🙏',
  '🎉',
  '✅',
  '❌',
  '🔥',
  '💡',
  '📌',
  '📎',
  '🤝',
]

const emit = defineEmits<{
  send: [text: string]
  image: [file: File]
  file: [file: File]
}>()

const draft = ref('')
const inputRef = ref<InputInstance>()
const imageInputRef = ref<HTMLInputElement>()
const fileInputRef = ref<HTMLInputElement>()

const canSend = computed(() => Boolean(draft.value.trim()))

function insertEmoji(emoji: string) {
  draft.value += emoji
}

async function insertMention(name: string) {
  draft.value = appendMemberMention(draft.value, name)
  await nextTick()
  inputRef.value?.focus?.()
}

defineExpose({ insertMention })

function submitText() {
  const text = draft.value.trim()
  if (!text) return
  emit('send', text)
  draft.value = ''
}

function onKeydown(event: KeyboardEvent) {
  if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return
  event.preventDefault()
  submitText()
}

function pickImage() {
  imageInputRef.value?.click()
}

function pickFile() {
  fileInputRef.value?.click()
}

function onImageChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (file) emit('image', file)
}

function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (file) emit('file', file)
}
</script>
